<template>
  <div class="creative-detail">
    <AlertWraper :content="content" :type="content_type" />
    <div class="">
      <!-- DETAILS -->
      <template v-if="!isEdit">
        <h3 class="font-size-xRegular text-uppercase mb-3">{{ $t('general_data') }}</h3>
        <table class="table align-middle table-row-dashed fs-6 gy-2 dataTable w-0">
          <tbody>
            <tr>
              <th class="bg-white fw-600">{{ $t('name') }}</th>
              <td>{{ localCreative.name }}</td>
            </tr>
            <tr>
              <th class="bg-white fw-600">{{ $t('destination_url') }}</th>
              <td class="text-break">{{ Array.isArray(localCreative.url) ? localCreative.url[0] : localCreative.url }}</td>
            </tr>
            <tr>
              <th class="bg-white fw-600">{{ $t('format') }}</th>
              <td>{{ localCreative.format | formatCreativeFormat }}</td>
            </tr>
            <tr v-if="localCreative.format === 'AUDIO'">
              <th class="bg-white fw-600">{{ $t('duration') }}</th>
              <td>{{ localCreative.duration }} {{ $t('seconds') }}</td>
            </tr>
            <tr v-if="localCreative.format !== 'YOUTUBE' && localCreative.format !== 'AUDIO'">
              <th class="bg-white fw-600">{{ $t('size') }}</th>
              <td>{{ localCreative.layout }}</td>
            </tr>
            <tr v-if="['NATIVE_DISPLAY', 'PUSH', 'YOUTUBE'].includes(localCreative.format) && localCreative.format !== 'YOUTUBE'">
              <th class="bg-white fw-600">{{ $t('advertisers_display_name') }}</th>
              <td>{{ localCreative.advertiser_display_name }}</td>
            </tr>
            <tr v-if="['NATIVE_DISPLAY', 'PUSH', 'YOUTUBE'].includes(localCreative.format) && localCreative.format !== 'YOUTUBE'">
              <th class="bg-white fw-600">{{ $t('caption_url') }}</th>
              <td>{{ localCreative.caption_url }}</td>
            </tr>
            <tr v-if="['NATIVE_DISPLAY', 'PUSH', 'YOUTUBE'].includes(localCreative.format)">
              <th class="bg-white fw-600">{{ $t('title') }}</th>
              <td>{{ localCreative.headline }}</td>
            </tr>
            <tr v-if="['NATIVE_DISPLAY', 'PUSH', 'YOUTUBE'].includes(localCreative.format)">
              <th class="bg-white fw-600">{{ $t('body_text') }}</th>
              <td>{{ localCreative.body_text }}</td>
            </tr>
            <tr v-if="['NATIVE_DISPLAY', 'PUSH', 'YOUTUBE'].includes(localCreative.format)">
              <th class="bg-white fw-600">{{ $t('call_to_action') }}</th>
              <td>{{ localCreative.call_to_action }}</td>
            </tr>
            <tr v-if="localCreative.format === 'SEARCH'">
              <th class="bg-white fw-600">{{ $t('titles') }}</th>
              <td>
                <ul class="mb-0">
                  <li v-for="(headline, index) in localCreative.headline" :key="index">{{ headline }}</li>
                </ul>
              </td>
            </tr>
            <tr v-if="localCreative.format === 'SEARCH'">
              <th class="bg-white fw-600">{{ $t('body_text') }}</th>
              <td>
                <ul class="mb-0">
                  <li v-for="(text, index) in localCreative.body_text" :key="index">{{ text }}</li>
                </ul>
              </td>
            </tr>
            <tr>
              <th class="bg-white fw-600">{{ $t('status') }}</th>
              <td>{{ localCreative.status | formatStatus }}
                <span v-if="localCreative.error_message !== null && localCreative.status === 'ERROR'">
                  - {{ $t(`creative_upload_errors.${localCreative.error_message}`) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Creative Media -->
        <template v-if="localCreative.format !== 'SEARCH'">
          <h3 class="font-size-xRegular text-uppercase mt-3">{{ $t('creative') }}</h3>
          <div class="text-center">
            <a :href="localCreative.file" target="_blank">
              <img v-if="contentType === 'IMAGE'" :src="localCreative.file" :alt="localCreative.name" class="img-fluid" style="max-width: 300px;" />
              <video v-if="contentType === 'VIDEO'" controls width="300" height="250" autoplay muted>
                <source :src="localCreative.file" :alt="localCreative.name" type="video/mp4">
              </video>
              <div v-if="contentType === 'AUDIO'" class="d-flex flex-column align-items-center">
                <img :src="localCreative.companion_creative.file" v-if="localCreative.companion_creative" :alt="localCreative.name" class="img-fluid mb-2" style="max-width: 300px;" />
                <audio controls width="300">
                  <source :src="localCreative.file" type="audio/mpeg" />
                </audio>
              </div>
            </a>
            <div class="mt-3 text-center" v-if="localCreative.format === 'YOUTUBE'">
              <iframe width="460" height="215" :src="`https://www.youtube.com/embed/${localCreative.youtube_id}`" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
            </div>
          </div>
        </template>

        <!-- Logo -->
        <template v-if="localCreative.logo">
          <h3 class="font-size-xRegular text-uppercase mt-3">{{ $t('logo') }}</h3>
          <div class="text-center">
            <a :href="localCreative.logo" target="_blank">
              <img :src="localCreative.logo" :alt="localCreative.name" width="100" />
            </a>
          </div>
        </template>

        <!-- Edit Button -->
        <div class="text-center mt-4" v-if="!localCreative.is_read_only">
          <Button class="btn-color10 mt-15" @click="isEdit = true">
            {{ $t('edit') }}
          </Button>
        </div>
      </template> <!-- END DETAILS -->

      <!-- EDIT -->
      <template v-else>
        <UpsertCreativeForm
          :advertiserId="localCreative.advertiser.id"
          :creative="localCreative"
          :format="localCreative.format"
          @creative-updated="handleUpdated"
          @upsert-creative-error="handleError"
          @canceled="isEdit = false"
        />
      </template> <!-- END EDIT -->
    </div>
  </div>
</template>

<script>
import { parseErrorResponseToArray } from '@/utils'

import AlertWraper from '@/components/common/Alert/AlertWraper'
import Button from '@/components/common/Button/Button'
import UpsertCreativeForm from '@/components/creatives/UpsertCreativeForm'

export default {
  name: 'CreativeDetail',
  props: ['creative'],
  components: {
    AlertWraper,
    Button,
    UpsertCreativeForm
  },
  data () {
    return {
      content: null,
      content_type: 'error',
      isEdit: false,
      loader: false,
      localCreative: null
    }
  },
  created () {
    this.localCreative = this.creative
  },
  computed: {
    availability () {
      const availability = []
      if (this.localCreative.is_desktop) availability.push('Desktop')
      if (this.localCreative.is_mobile) availability.push('Mobile')
      if (this.localCreative.is_app) availability.push('Aplicativos')
      return availability.length > 0 ? availability.join(', ') : this.$t('no_availability')
    },
    contentType () {
      if (!this.localCreative) return ''
      if (['NATIVE_DISPLAY', 'DISPLAY', 'PUSH'].includes(this.localCreative.format)) return 'IMAGE'
      if (this.localCreative.format === 'DISPLAY_VIDEO') return 'VIDEO'
      if (this.localCreative.format === 'AUDIO') return 'AUDIO'
      return ''
    }
  },
  methods: {
    handleUpdated (creative) {
      this.isEdit = false
      this.localCreative = creative
      this.content = null
    },
    handleError (error) {
      this.content = parseErrorResponseToArray(error)
    }
  }
}
</script>

<style lang="scss" scoped>
.wrap-text {
  width: 100%;
  word-wrap: break-word;
}
.table th {
  width: 30%;
}
</style>
